/* eslint-disable */

/**
 * Two-step Width -> Length picker for Balterley "Size" option.
 *
 * Reads hidden radios (one per "W x Lmm" value) and renders two <select>s.
 * Changing Length sets the matching radio's .checked = true and dispatches
 * a bubbling 'change' event so <variant-picker> picks it up.
 *
 * Availability comes from the 'is-unavailable' class on the hidden radios,
 * which variant-picker.js#updateAvailability maintains.
 *
 * Dependencies: none.
 */

if (!customElements.get('size-two-step-picker')) {
  class SizeTwoStepPicker extends HTMLElement {
    constructor() {
      super();
      this._sizeRegex = /(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i;
      this._onWidthChange = this._onWidthChange.bind(this);
      this._onLengthChange = this._onLengthChange.bind(this);
      this._onVariantChange = this._onVariantChange.bind(this);
    }

    connectedCallback() {
      // If the script loads before the element's children are parsed,
      // connectedCallback fires with an empty element. Defer init until the
      // DOM is fully parsed so our querySelector calls find the children.
      if (document.readyState === 'loading') {
        this.ownerDocument.addEventListener('DOMContentLoaded', () => this._init());
      } else {
        this._init();
      }
    }

    _init() {
      if (this._initialized) return;

      this.widthSelect = this.querySelector('.js-width-select');
      this.lengthSelect = this.querySelector('.js-length-select');
      this.radios = Array.from(this.querySelectorAll('input.js-option'));

      if (!this.widthSelect || !this.lengthSelect || this.radios.length === 0) return;

      this._initialized = true;

      this._buildMap();
      this._populateWidths();
      this._syncFromCheckedRadio();

      this.widthSelect.addEventListener('change', this._onWidthChange);
      this.lengthSelect.addEventListener('change', this._onLengthChange);

      // Re-sync availability whenever the outer variant-picker fires a change.
      this._variantPicker = this.closest('variant-picker');
      if (this._variantPicker) {
        this._variantPicker.addEventListener('on:variant:change', this._onVariantChange);
      }

      // Fallback: observe class changes on radios (updateAvailability toggles
      // 'is-unavailable'). Cheap insurance in case the event path varies.
      this._observer = new MutationObserver(() => this._refreshAvailability());
      this.radios.forEach((radio) => {
        this._observer.observe(radio, { attributes: true, attributeFilter: ['class'] });
      });
    }

    disconnectedCallback() {
      if (this._observer) this._observer.disconnect();
      if (this._variantPicker) {
        this._variantPicker.removeEventListener('on:variant:change', this._onVariantChange);
      }
    }

    /**
     * Parse "1200 x 800mm" -> { width: 1200, length: 800 }.
     * @param {string} value
     * @returns {?{width:number,length:number}}
     */
    _parse(value) {
      if (!value) return null;
      const m = value.match(this._sizeRegex);
      if (!m) return null;
      return { width: parseFloat(m[1]), length: parseFloat(m[2]) };
    }

    /**
     * Build Map<width, Array<{length, radio, raw}>>.
     */
    _buildMap() {
      this.widthMap = new Map();
      this.radios.forEach((radio) => {
        const parsed = this._parse(radio.value);
        if (!parsed) return;
        const entry = { length: parsed.length, radio: radio, raw: radio.value };
        const arr = this.widthMap.get(parsed.width) || [];
        arr.push(entry);
        this.widthMap.set(parsed.width, arr);
      });

      // Sort each width's lengths ascending.
      this.widthMap.forEach((arr) => arr.sort((a, b) => a.length - b.length));
    }

    /**
     * Populate the Width dropdown with unique widths sorted ascending.
     */
    _populateWidths() {
      const widths = Array.from(this.widthMap.keys()).sort((a, b) => a - b);
      const current = this.widthSelect.value;

      // Clear existing non-placeholder options.
      this.widthSelect.innerHTML = '<option value="">Select width&hellip;</option>';

      widths.forEach((w) => {
        const opt = document.createElement('option');
        opt.value = String(w);
        opt.textContent = this._formatWidth(w);
        this.widthSelect.appendChild(opt);
      });

      if (current && widths.indexOf(parseFloat(current)) !== -1) {
        this.widthSelect.value = current;
      }
    }

    /**
     * Populate the Length dropdown for the given width. Greys out unavailable lengths.
     * @param {number|string} width
     * @param {?string} preserveRaw - raw "W x Lmm" value to keep selected if still valid.
     */
    _populateLengths(width, preserveRaw) {
      const numWidth = parseFloat(width);
      const entries = this.widthMap.get(numWidth) || [];

      this.lengthSelect.innerHTML = '<option value="">Select length&hellip;</option>';

      entries.forEach((entry) => {
        const opt = document.createElement('option');
        opt.value = entry.raw;
        const unavailable = entry.radio.classList.contains('is-unavailable');
        opt.textContent = unavailable
          ? `${this._formatLength(entry.length)} (Out of stock)`
          : this._formatLength(entry.length);
        opt.disabled = unavailable;
        opt.dataset.length = String(entry.length);
        this.lengthSelect.appendChild(opt);
      });

      this.lengthSelect.disabled = entries.length === 0;

      if (preserveRaw) {
        const match = Array.from(this.lengthSelect.options).find(
          (o) => o.value === preserveRaw && !o.disabled
        );
        if (match) this.lengthSelect.value = preserveRaw;
      }
    }

    /**
     * On load, find the already-checked radio (honours ?variant=) and set
     * Width + Length selects accordingly.
     */
    _syncFromCheckedRadio() {
      const checked = this.radios.find((r) => r.checked);
      if (!checked) return;
      const parsed = this._parse(checked.value);
      if (!parsed) return;

      this.widthSelect.value = String(parsed.width);
      this._populateLengths(parsed.width, checked.value);
    }

    /**
     * Width <select> change handler.
     */
    _onWidthChange() {
      const width = this.widthSelect.value;
      if (!width) {
        this.lengthSelect.innerHTML = '<option value="">Select length&hellip;</option>';
        this.lengthSelect.disabled = true;
        return;
      }
      this._populateLengths(width, null);
    }

    /**
     * Length <select> change handler. Flips the matching hidden radio and
     * fires a bubbling 'change' so <variant-picker> reacts.
     */
    _onLengthChange() {
      const raw = this.lengthSelect.value;
      if (!raw) return;

      const radio = this.radios.find((r) => r.value === raw);
      if (!radio) return;

      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
    }

    /**
     * Re-sync disabled states on the current Length dropdown after a variant change.
     */
    _onVariantChange() {
      this._refreshAvailability();
    }

    /**
     * Rewalk the currently-shown length options and flip their disabled state
     * + "(Out of stock)" suffix from the radios' 'is-unavailable' class.
     * Preserves current selection if still valid.
     */
    _refreshAvailability() {
      if (!this.lengthSelect || this.lengthSelect.disabled) return;
      const width = this.widthSelect && this.widthSelect.value;
      if (!width) return;

      const currentRaw = this.lengthSelect.value;
      this._populateLengths(width, currentRaw);
    }

    _formatWidth(w) {
      return `${this._formatNumber(w)}mm`;
    }

    _formatLength(l) {
      return `${this._formatNumber(l)}mm`;
    }

    _formatNumber(n) {
      return Number.isInteger(n) ? String(n) : String(n);
    }
  }

  customElements.define('size-two-step-picker', SizeTwoStepPicker);
}
